import React from 'react';
import { Skeleton, Box, Stack, Card, CardContent, useTheme } from '@mui/material';

interface SkeletonCardProps {
  children?: React.ReactNode;
  height?: string | number;
  sx?: any;
  contentSx?: any;
}

const AllTradesPageSkeleton = () => {
  const theme = useTheme();

  const SkeletonCard = ({ children, height, sx = {}, contentSx = {} }: SkeletonCardProps) => (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 4,
        border: `1px solid ${theme.palette.divider}`,
        ...sx
      }}
    >
      <CardContent
        sx={{
          height: '100%',
          p: { xs: 1.5, md: 2 },
          '&:last-child': { pb: { xs: 1.5, md: 2 } },
          ...contentSx
        }}
      >
        {children ? children : <Skeleton variant="rectangular" height={height || '100%'} sx={{ borderRadius: 2 }} />}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 0 }}>
      <Box mb={{ xs: 2, md: 3 }} ml={{ xs: 0.5, md: 1.5 }}>
        <Skeleton variant="text" width={160} height={60} />
      </Box>

      <SkeletonCard sx={{ minHeight: 600 }}>
        <Stack spacing={{ xs: 1.5, md: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={{ xs: 1, sm: 0 }}
          >
            <Box display="flex" alignItems="center" width={{ xs: '100%', sm: 'auto' }}>
              <Skeleton variant="circular" width={28} height={28} sx={{ mr: 1 }} />
              <Skeleton variant="text" width={140} height={32} />
            </Box>

            <Box width={{ xs: '100%', sm: 'auto' }}>
              <Skeleton
                variant="rectangular"
                height={32}
                sx={{
                  borderRadius: 2,
                  width: { xs: '100%', sm: 280 }
                }}
              />
            </Box>
          </Box>

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            flexDirection={{ xs: 'column', sm: 'row' }}
            gap={{ xs: 1, sm: 0 }}
          >
            <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1.5, width: { xs: '100%', sm: 300 } }} />

            <Box display={{ xs: 'none', sm: 'flex' }} gap={1} mt={{ xs: 0, sm: 0 }}>
              <Skeleton variant="rectangular" width={140} height={40} sx={{ borderRadius: 1.5, flex: { xs: 1, sm: 'none' } }} />
              <Skeleton variant="rectangular" width={40} height={40} sx={{ borderRadius: 1.5 }} />
            </Box>
          </Box>

          <Stack spacing={1}>
            <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1, opacity: 0.6, mb: 0.5 }} />

            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} variant="rectangular" height={45} sx={{ borderRadius: 1 }} />
            ))}
          </Stack>
        </Stack>
      </SkeletonCard>
    </Box>
  );
};

export default AllTradesPageSkeleton;
